/**
 * API Service — gọi Django backend và normalize data
 * snake_case (API) → camelCase (React components)
 * Hỗ trợ đa ngôn ngữ: chọn trường _vn/_en theo lang hiện tại
 */

import i18n from '../i18n/i18n';
import { api } from '../api/client';
import { API_BASE } from './servicesDomain';
import { getSession } from '../utils/authStorage';

// ── Helper: chọn trường theo ngôn ngữ hiện tại ──────────────────────────────
const getLang = () => (i18n.language?.startsWith('en') ? 'en' : 'vn');

/** Pick localized field: ưu tiên ngôn ngữ hiện tại, fallback ngôn ngữ kia */
const L = (obj, fieldBase) => {
  const lang = getLang();
  const other = lang === 'en' ? 'vn' : 'en';
  return obj[`${fieldBase}_${lang}`] || obj[fieldBase] || obj[`${fieldBase}_${other}`] || '';
};

// ── Normalize product từ API sang format React components đang dùng ──────────
export const normalizeProduct = (p) => {
  // Schema mới: ưu tiên image_a/image_b; fallback images[].image; giữ tương thích schema cũ.
  const imageA = p.image_a || p.images?.[0]?.image || p.gallery?.[0]?.url || null;
  const imageB = p.image_b || p.images?.[1]?.image || p.gallery?.[1]?.url || imageA;

  const base = {
    id: p.id,
    slug: p.slug,
    name: L(p, 'name') || p.name || '',
    description: L(p, 'description') || p.description || '',
    price: typeof p.price === 'string' ? parseFloat(p.price) : (p.price ?? 0),
    volume: p.volume || '',
    rating: p.rating ?? 4.5,
    reviews: p.review_count ?? 0,
    imageA: imageA,
    imageB: imageB,
    isBestSeller: p.is_bestseller ?? false,
    isFeatured: p.is_featured ?? false,
    category: p.category || 'Skincare',
  };

  // Gallery (nếu có)
  if (p.images?.length || p.gallery?.length) {
    const imageList = p.images?.length ? p.images : p.gallery;
    base.gallery = imageList.map((img) => ({
      type: img.image_type,
      url: img.image || img.url,
      label: img.caption || '',
    }));
  }

  // Chi tiết sản phẩm (chỉ có ở ProductDetail endpoint)
  if (p.benefits !== undefined) {
    const clinicalResults =
      p.science?.clinical_results || p.clinical_results || [];

    // Localize clinical results labels
    const localizedClinicalResults = clinicalResults.map((cr) => ({
      ...cr,
      label: L(cr, 'label') || cr.label || '',
    }));

    base.details = {
      benefits: (p.benefits || []).map((b) => L(b, 'text') || b.text || ''),
      science: p.science
        ? {
          result_text: L(p.science, 'result') || p.science.result_text || '',
          before_after_image: p.science.image || p.science.before_after_image || null,
          clinical_results: localizedClinicalResults,
        }
        : null,
      mechanisms: (p.mechanisms || []).map((m) => ({
        title: L(m, 'title') || m.title || '',
        content: L(m, 'content') || m.content || '',
        image: m.image || null,
      })),
      ingredients: (p.ingredients || []).map((ing) =>
        typeof ing === 'string'
          ? ing
          : {
            name: L(ing, 'name') || ing.name || '',
            description: L(ing, 'description') || ing.description || '',
            image: ing.image || null,
          }
      ),
      usage: p.usage
        ? {
          steps: (p.usage.steps || []).map((s) =>
            typeof s === 'string' ? s : (L(s, 'step') || s.step || '')
          ),
          note: L(p.usage, 'note') || p.usage.note || '',
          image: p.usage.image || p.usage.guide_image || null,
        }
        : null,
    };
  }

  return base;
};

// ── Helper fetch với timeout ──────────────────────────────────────────────────
const apiFetch = async (path, timeout = 5000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(`${API_BASE}${path}`, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
};

// ── API calls ─────────────────────────────────────────────────────────────────

/** GET /api/products/featured/ — custom action trả về plain array */
export const fetchFeatured = () =>
  apiFetch('/products/featured/').then((data) =>
    (Array.isArray(data) ? data : data.results ?? []).map(normalizeProduct)
  );

/** GET /api/products/bestsellers/ — custom action trả về plain array */
export const fetchBestsellers = () =>
  apiFetch('/products/bestsellers/').then((data) =>
    (Array.isArray(data) ? data : data.results ?? []).map(normalizeProduct)
  );

/** GET /api/products/ — có thể trả về paginated hoặc array */
export const fetchProducts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/products/${qs ? '?' + qs : ''}`).then((data) =>
    (Array.isArray(data) ? data : data.results ?? []).map(normalizeProduct)
  );
};

/** GET /api/products/{slug}/ */
export const fetchProductBySlug = (slug) =>
  apiFetch(`/products/${slug}/`).then(normalizeProduct);

/** GET /api/products/{id}/reviews/ */
export const fetchProductReviews = (productId) =>
  apiFetch(`/products/${productId}/reviews/`).then((data) =>
    Array.isArray(data) ? data : data.results ?? []
  );

function parseReviewCreateError(body) {
  if (typeof body.detail === 'string') return body.detail;
  if (Array.isArray(body.detail)) {
    return body.detail.map((d) => (typeof d === 'string' ? d : d?.message || JSON.stringify(d))).join(' ');
  }
  if (body?.message) return body.message;
  if (body && typeof body === 'object') {
    const parts = [];
    Object.entries(body).forEach(([key, val]) => {
      if (key === 'detail') return;
      if (Array.isArray(val)) parts.push(`${key}: ${val.join(' ')}`);
      else if (typeof val === 'string') parts.push(`${key}: ${val}`);
    });
    if (parts.length) return parts.join(' ');
  }
  return null;
}

/** POST /api/products/{id}/reviews/ — JWT; không ảnh: JSON, có ảnh: multipart */
export async function createProductReview(productId, { json, formData }) {
  const access = getSession()?.access;
  const authHeaders = access ? { Authorization: `Bearer ${access}` } : {};
  const url = `${API_BASE}/products/${productId}/reviews/`;
  const res =
    formData != null
      ? await fetch(url, { method: 'POST', headers: authHeaders, body: formData })
      : await fetch(url, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(json),
        });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(parseReviewCreateError(body) || `HTTP ${res.status}`);
  }
  return body;
}

export const postReviewHelpful = (reviewId, vote) =>
  api.post(`/api/reviews/${reviewId}/helpful/`, { vote }).then((r) => r.data);

export const postReviewReport = (reviewId) =>
  api.post(`/api/reviews/${reviewId}/report/`).then((r) => r.data);
